import React from 'react';

/**
 * Focus-on-mount for the one field a screen or modal is meant to start on.
 *
 * The clinic app's keyboard doctrine (clinic-app/CLAUDE.md §4.2) is that focus
 * lands on the first field automatically: a modal that opens with focus still on
 * <body> costs the desk a mouse trip before it can type a single character, and
 * the ثبت نوبت flow is opened by a keystroke (F4) to begin with.
 *
 * Attach the returned ref to the input. Focus follows the *node*, so a field that
 * unmounts and comes back — switching back to «بیمار قبلی», hitting «تغییر» on a
 * chosen patient — is focused again, which is what the desk expects both times.
 *
 * Pass `enabled: false` to opt a render out (e.g. a field that is not the one the
 * screen starts on in that particular state).
 */
export function useAutoFocus(enabled = true) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!enabled) return undefined;
    // A frame later, not synchronously: modals here mount mid-animation, and the
    // caret is more reliable once the tree the field lives in has painted once.
    const id = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      // Whatever was typed before is replaced by the next keystroke rather than
      // appended to — returning to the field means starting a new search.
      el.select?.();
    });
    return () => cancelAnimationFrame(id);
  }, [enabled]);

  return ref;
}

export default useAutoFocus;
